import { Avatar, Box, Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router';
import { MetaData } from '@sendbird/chat';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import SupplierPlaceholderImage from '../../assets/placeholders/supplier-placeholder.png';
import { useGetChannelSupplierInfoMessageDataQuery } from '../../queries/__generated__/getChannelSupplierInfoMessageData.generated';
import { currentContextAtom } from '../../state';
import { RoutePaths } from '../../routes/AppRoutes';
import { CompanyDeliveryZone, getDeliveryZoneFromAdministrativeArea } from '../../common/utils/common.utils';
import { GeoLoc } from '../../common/models/user.models';
import { MinimumOrderValue } from '../../common/state/state';
import FileUtils from '../../common/utils/file.utils';
import { PackagingFamilyTypeIntl } from '../../common/i18n/product-family.translation';
import SocialTags from '../../social-proof/SocialTags';
import InfoSocialTags from '../../social-proof/InfoSocialTags';
import CompanyServicesInfoLight from '../../company/companyInfo/CompanyServicesInfoLight';
import DiscoveryDialogPackagings from '../../discovery/as-buyer/DiscoveryDialogPackagings';
import { currentChannelAtom } from '../state';

export default function SupplierInfoMessage(): React.JSX.Element {
  const { t } = useTranslation(['chat', 'contracts', 'product-family']);
  const { realm } = useAtomValue(currentContextAtom);
  const currentChannel = useAtomValue(currentChannelAtom);
  const { data } = useGetChannelSupplierInfoMessageDataQuery({
    variables: {
      supplierId: (currentChannel!.cachedMetaData as MetaData).supplyingCompanyId,
      buyerId: (currentChannel!.cachedMetaData as MetaData).buyingCompanyId
    },
  });

  const deliveryZone = useMemo((): CompanyDeliveryZone | undefined  => {
    if (!data?.supplier) {
      return
    }

    return getDeliveryZoneFromAdministrativeArea(data.supplier?.delivery_zones, data.buyer!.addresses[0].administrativeAreaLevel2!);
  }, [realm, data]);

  const minimumOrderValue = useMemo((): MinimumOrderValue | undefined => {
    if (!deliveryZone) {
      return;
    }

    return {
      value: deliveryZone.minimum_order_value,
      type: deliveryZone.minimum_order_value_type,
      negotiable: deliveryZone.negotiable_minimum_order_value,
    };
  }, [deliveryZone]);

  const targetGeoLoc = useMemo((): GeoLoc | undefined => {
    if (!data?.supplier) {
      return;
    }

    const address = realm === Business_Profile_Enum.Buyer ? data.supplier.addresses[0] : data.buyer!.addresses[0];

    return {
      lat: address.latitude,
      lng: address.longitude,
    };
  }, [realm, data]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: realm === Business_Profile_Enum.Buyer ? 'start' : 'end',
        gap: 0.5,
        mb: 3,
      }}
    >
      <Typography variant="bodySmall">
        {t(realm === Business_Profile_Enum.Buyer ? 'chat:supplier_card' : 'chat:your_supplier_card')}
      </Typography>
      {data?.supplier && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 1.5,
            borderRadius: 4,
            backgroundColor: 'surfaceContainer.main',
            minWidth: {
              lg: 539,
            },
            maxWidth: 'calc(100% - 24px)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Avatar
                  size={48}
                  src={FileUtils.BuildCompanyImageUrl({
                    photo: data.supplier.photos[0]?.photo,
                    size: {
                      width: 48,
                      height: 48,
                    },
                    type: Business_Profile_Enum.Supplier,
                  })}
                >
                  <img width="48" height="48" src={SupplierPlaceholderImage} />
                </Avatar>
                <Box>
                  <Typography variant="titleSmall">
                    {data.supplier.tradeName}
                  </Typography>
                  {!!data.supplier.products.length && (
                    <Typography variant="bodySmall" color="onSurfaceVariant">
                      {t(`product-family:${PackagingFamilyTypeIntl[data.supplier.products_aggregate.aggregate!.max!.sub_family as Product_Sub_Family_Name_Enum]}`)}
                      {data.supplier.products.length > 1 && (
                        <> (+{data.supplier.products.length - 1})</>
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <InfoSocialTags
                  isWholesaler={data.supplier.is_wholesaler}
                  targetGeoLoc={targetGeoLoc!}
                  minimumOrderValue={minimumOrderValue}
                  deliveryTime={deliveryZone?.delivery_time || undefined}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <SocialTags tags={data.supplier.social_tags} displayRules={{ rules: { 1: 1, 2: 2 } }} />
            </Box>
          </Box>
          <DiscoveryDialogPackagings companyId={(currentChannel!.cachedMetaData as MetaData).supplyingCompanyId} />
          <CompanyServicesInfoLight
            services={data.supplier}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              backgroundColor: 'inherit',
              overflow: 'unset',
              py: 1,
              px: 1.5,
              '& .Kheops-CompanyServicesInfoLight-toggle': {
                display: 'none',
              },
            }}
          />
          {realm === Business_Profile_Enum.Buyer && (
            <Button
              sx={{ alignSelf: 'end' }}
              variant="outlined"
              component={Link}
              to={
                generatePath(RoutePaths.CONTRACT_COMPANY, { companyId: data.supplier.id })
              }
            >
              {t('contracts:see_supplier_page')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
